import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { applySeo, getSiteUrl, SITE_FAVICON, type RouteSeoData } from "@/lib/seo";
import { pillarArticles, type ArticleLocale } from "@/mocks/articles";

type LocaleKey = "en" | "fr" | "da" | "de";

function pickLocale(
  lang: string,
  locales: { en: ArticleLocale; fr: ArticleLocale; da: ArticleLocale; de: ArticleLocale },
): ArticleLocale {
  const key = (lang.split("-")[0] || "en") as LocaleKey;
  return locales[key] ?? locales.en;
}

const HOME_KEYWORDS =
  "allchinabuy spreadsheet, allchinabuy review, allchinabuy shipping, allchinabuy qc, allchinabuy finds, allchinabuy buying guide";

export default function SeoManager() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const origin = getSiteUrl();
    let data: RouteSeoData;

    if (pathname === "/") {
      const title = t("hero.title");
      const description = t("hero.subtitle");
      data = {
        title,
        description,
        keywords: HOME_KEYWORDS,
        canonical: `${origin}/`,
        ogTitle: title,
        ogDescription: description,
        ogType: "website",
        ogImage: SITE_FAVICON,
        twitterImage: SITE_FAVICON,
      };
    } else if (pathname === "/allchinabuy-spreadsheet-guide") {
      const title = t("metaTitle");
      const description = t("metaDescription");
      data = {
        title,
        description,
        keywords:
          "allchinabuy spreadsheet, allchinabuy buying guide, allchinabuy qc, allchinabuy seller, allchinabuy shipping",
        canonical: `${origin}/allchinabuy-spreadsheet-guide`,
        ogTitle: title,
        ogDescription: description,
        ogType: "article",
      };
    } else if (pathname.startsWith("/articles/")) {
      const slug = pathname.slice("/articles/".length);
      const article = pillarArticles.find((a) => a.slug === slug);
      if (article) {
        const locale = pickLocale(i18n.language, article.locales);
        data = {
          title: locale.metaTitle,
          description: locale.metaDescription,
          keywords: `allchinabuy ${slug.replace(/-/g, " ")}, allchinabuy spreadsheet, allchinabuy guide`,
          canonical: `${origin}/articles/${slug}`,
          ogTitle: locale.metaTitle,
          ogDescription: locale.metaDescription,
          ogType: "article",
          ogImage: article.image,
          twitterImage: article.image,
        };
      } else {
        data = {
          title: "Page Not Found",
          description: "",
          keywords: "",
          canonical: `${origin}${pathname}`,
          ogTitle: "Page Not Found",
          ogDescription: "",
          ogType: "website",
          noindex: true,
        };
      }
    } else {
      data = {
        title: "Page Not Found",
        description: "",
        keywords: "",
        canonical: `${origin}${pathname}`,
        ogTitle: "Page Not Found",
        ogDescription: "",
        ogType: "website",
        noindex: true,
      };
    }

    applySeo(data);
  }, [pathname, i18n.language, t]);

  return null;
}